import React, {useEffect, useRef, useState} from "react";
import {useLocation, useParams} from "react-router-dom";
import {Box, Button, Typography} from "@material-ui/core";
import Glossary from "view/apps/Organiser/pages/Show/Glossary/Glossary";
import PoweredByTitle from "view/components/PoweredByTitle";
import allCategory from "view/apps/Organiser/pages/Show/Glossary/allCategory";
import {scrollToTermById} from "view/apps/Organiser/pages/Show/Glossary/useScrollToTerm";
import useGlossary from "hooks/show/useGlossary";
import {ICategory, Roles} from "@evennt-tech/api-v2-core";
import {useSelector} from "react-redux";
import {selectUser} from "store/auth/selectors";
import GlossarySearchBar from "view/GlossaryPage/SearchBar";
import FlexBox from "view/components/Layout/FlexBox";
import EmptyResultsView from "view/components/EmptyResultsView";
import {useGlossarySearchQuery} from "store/glossary/glossary.store.selectors";
import {useGlossarySearch} from "hooks/show/glossary/useGlossarySearch";
import useGlossaryPrint from "hooks/show/glossary/useGlossaryPrint";
import useImagesLoading from "hooks/show/glossary/useImagesLoading";
import BackdropLoader from "view/components/BackdropLoader";
import {useShows} from "store/show/show.store";
import {shallow} from "zustand/shallow";
import PrintIcon from "@material-ui/icons/Print";
import {flushSync} from "react-dom";
import getSearchParamsObject from "tools/common/getSearchParamsObject";
import {useTolgee, useTranslate} from "@tolgee/react";
import {usePublishedLanguages} from "hooks";
import {useCurrentExhibitor} from "store/current-exhibitor/current-exhibitor.store";
import useStyles from "./styles";
import TopBar from "./TopBar";
import {resolveStandaloneLanguage} from "./resolveStandaloneLanguage";

type SearchParams = {
  buildTypeId?: string;
  autoPrint?: string;
  lang?: string;
};

const GlossaryPage = () => {
  const classes = useStyles();

  const {showId} = useParams<{showId: string}>();
  const location = useLocation();
  const {
    buildTypeId: searchParamBuildTypeId,
    autoPrint: searchParamAutoPrint,
    lang: langParam,
  } = getSearchParamsObject<SearchParams>(new URLSearchParams(location.search));
  const shouldAutoPrint = searchParamAutoPrint === "1";
  const searchQuery = useGlossarySearchQuery();
  const autoCatchShow = useShows((state) => state.autoCatchOne, shallow);
  const {isPrintView, print, printWhenReady} = useGlossaryPrint();
  const hasAutoPrinted = useRef(false);

  const [selectedCategory, setSelectedCategory] = useState<ICategory | null>(
    allCategory
  );
  const rawGlossary = useGlossary({
    showId,
    localised: true,
    ...(searchParamBuildTypeId && {buildTypeId: searchParamBuildTypeId}),
  });

  const show = rawGlossary.publicShow;

  const tolgee = useTolgee();
  const {t} = useTranslate();
  const {codes: availableLanguageCodes} = usePublishedLanguages();
  const exhibitorSelectedLanguage = useCurrentExhibitor(
    (state) => state.item?.user?.selectedLanguage
  );
  const fetchExhibitor = useCurrentExhibitor((state) => state.fetch);
  const isExhibitor = useSelector(selectUser).role === Roles.EXHIBITOR;
  const [isExhibitorResolved, setIsExhibitorResolved] = useState(false);

  // This public route sits outside the exhibitor app, which is what loads the
  // current exhibitor elsewhere; their own language decides the link (FS-3279).
  useEffect(() => {
    if (!isExhibitor) return;

    fetchExhibitor().finally(() => setIsExhibitorResolved(true));
  }, [isExhibitor, fetchExhibitor]);

  useEffect(() => {
    if (!show || (isExhibitor && !isExhibitorResolved)) return;

    tolgee.changeLanguage(
      resolveStandaloneLanguage({
        exhibitorLanguage: exhibitorSelectedLanguage,
        linkLanguage: langParam,
        showDefaultLanguage: show.defaultLanguage,
        availableCodes: availableLanguageCodes,
      })
    );
  }, [
    show,
    isExhibitor,
    isExhibitorResolved,
    exhibitorSelectedLanguage,
    langParam,
    availableLanguageCodes,
    tolgee,
  ]);

  const isLoadingImages = useImagesLoading([
    rawGlossary.terms,
    selectedCategory?.id,
  ]);

  const {terms, categories} = useGlossarySearch({
    terms: rawGlossary.terms,
    categories: rawGlossary.categories,
    setSelectedCategory,
    ...(selectedCategory &&
      selectedCategory?.id !== "all" && {selectedCategory}),
    show,
  });
  const glossary = {...rawGlossary, terms, categories};

  const [scrollToTermId, setScrollToTermId] = useState<string | undefined>(
    undefined
  );

  useEffect(() => {
    if (!showId) return;

    return autoCatchShow(showId);
  }, [showId]);

  useEffect(() => {
    if (!shouldAutoPrint || hasAutoPrinted.current) return;
    if (rawGlossary.isLoadingGlossary || !rawGlossary.glossary) return;
    if (rawGlossary.isLoadingPublicShow) return;

    hasAutoPrinted.current = true;
    printWhenReady();
  }, [
    shouldAutoPrint,
    rawGlossary.isLoadingGlossary,
    rawGlossary.glossary,
    rawGlossary.isLoadingPublicShow,
  ]);

  const selectedTerm = (category: ICategory, termId: string) => {
    setSelectedCategory(category);
    setScrollToTermId(termId);

    scrollToTermById({
      termId,
      categoryId: category.id,
      terms: glossary.terms,
    });
  };

  const scrollToTerm = () => {
    scrollToTermById({
      termId: scrollToTermId,
      categoryId: selectedCategory?.id,
      terms: glossary.terms,
    });
  };

  const content =
    searchQuery && !glossary.terms.length ? (
      <EmptyResultsView searchQuery={searchQuery} />
    ) : (
      <Glossary
        selectedTerm={selectedTerm}
        scrollToTerm={scrollToTerm}
        showGlossary={glossary}
        setScrollToTermId={(termId) => setScrollToTermId(termId)}
        setSelectedCategory={(category) => setSelectedCategory(category)}
        showId={showId}
        selectedCategory={selectedCategory}
        readonly
        isPrintView={isPrintView}
      />
    );

  return (
    <Box className={classes.pageContainer}>
      <BackdropLoader
        isLoading={
          rawGlossary.isLoadingGlossary ||
          rawGlossary.isLoadingPublicShow ||
          isLoadingImages
        }
      />
      <TopBar
        title={show?.name}
        showLogoUrl={show?.logoFile?.url}
        organiserLogoUrl={show?.organiserCompany?.logo?.url}
      />
      <FlexBox column gap={3} fullWidth className={classes.glossaryContainer}>
        <FlexBox
          alignCenter
          fullWidth
          justifyBetween
          className={classes.noPrint}
        >
          <Typography variant="subtitle1">
            {t("exhibitor_show_info_pg.page_name.show_info")}
          </Typography>
          <Button
            color="primary"
            startIcon={<PrintIcon color="primary" />}
            onClick={print}
            disabled={rawGlossary.isLoadingGlossary || !rawGlossary.glossary}
          >
            {t({key: "exhibitor_show_info_pg.print", defaultValue: "Print"})}
          </Button>
        </FlexBox>
        <GlossarySearchBar />
        {content}
      </FlexBox>
      <Box display="flex" justifyContent="center">
        <PoweredByTitle />
      </Box>
    </Box>
  );
};

export default GlossaryPage;
